import {useCallback, useEffect, useState} from "react";
import {Alert, Button, Card, Col, Container, Form, Row, Spinner, Tab, Tabs} from "react-bootstrap";
import PropTypes from "prop-types";
import {getAllowedRegion} from "../../service/UserService";
import {
    createDurationType, createServiceArea, createSkill, getWorkerCatalogs,
    readWorkerSettingsJson, updateDurationType, updateServiceArea, updateSkill
} from "../../service/WorkerSettingsService";

const EMPTY_CATALOGS = {skills: [], serviceAreas: [], durationTypes: []};

export default function WorkerSettings() {
    const [catalogs, setCatalogs] = useState(EMPTY_CATALOGS);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [newSkill, setNewSkill] = useState({name: '', status: 'ACTIVE'});
    const [newArea, setNewArea] = useState({name: '', postcode: '', operational_region: '', status: 'ACTIVE'});
    const [newDuration, setNewDuration] = useState({name: '', default_duration_minutes: 120, status: 'ACTIVE'});

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [loadedCatalogs, regionResponse] = await Promise.all([
                getWorkerCatalogs(),
                getAllowedRegion(),
            ]);
            const loadedRegions = await readWorkerSettingsJson(regionResponse, 'Failed to load Regions');
            setCatalogs(loadedCatalogs);
            setRegions(loadedRegions);
            setNewArea(current => ({...current, operational_region: current.operational_region || loadedRegions[0] || ''}));
        } catch (loadError) {
            setError(loadError.message || 'Failed to load Worker Settings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    function changeRow(collection, id, patch) {
        setCatalogs(current => ({
            ...current,
            [collection]: current[collection].map(item => item.id === id ? {...item, ...patch} : item),
        }));
    }

    async function mutate(key, request, collection) {
        setBusy(key);
        setError(null);
        setMessage(null);
        try {
            const response = await request();
            const saved = await readWorkerSettingsJson(response, 'Worker Setting update failed');
            setCatalogs(current => ({
                ...current,
                [collection]: current[collection].some(item => item.id === saved.id)
                    ? current[collection].map(item => item.id === saved.id ? saved : item)
                    : [...current[collection], saved],
            }));
            setMessage('Worker Setting saved.');
            return saved;
        } catch (mutationError) {
            setError(mutationError.message || 'Worker Setting update failed');
            return null;
        } finally {
            setBusy(null);
        }
    }

    async function addSkill(event) {
        event.preventDefault();
        if (await mutate('new-skill', () => createSkill(newSkill), 'skills')) {
            setNewSkill({name: '', status: 'ACTIVE'});
        }
    }

    async function addArea(event) {
        event.preventDefault();
        if (await mutate('new-area', () => createServiceArea(newArea), 'serviceAreas')) {
            setNewArea(current => ({name: '', postcode: '', operational_region: current.operational_region, status: 'ACTIVE'}));
        }
    }

    async function addDuration(event) {
        event.preventDefault();
        if (await mutate('new-duration', () => createDurationType(newDuration), 'durationTypes')) {
            setNewDuration({name: '', default_duration_minutes: 120, status: 'ACTIVE'});
        }
    }

    if (loading) {
        return <div className="text-center py-5"><Spinner animation="border" /><span className="visually-hidden">Loading Worker Settings</span></div>;
    }

    return (
        <Container className="py-4 worker-settings-page">
            <Card className="shadow-sm">
                <Card.Header className="bg-primary text-white">
                    <h1 className="h5 mb-0">Worker Settings</h1>
                </Card.Header>
                <Card.Body>
                    {error ? <Alert variant="danger">{error}</Alert> : null}
                    {message ? <Alert variant="success">{message}</Alert> : null}
                    <Tabs defaultActiveKey="skills" className="mb-3">
                        <Tab eventKey="skills" title="Skills">
                            <Form onSubmit={addSkill} className="worker-setting-add-row mb-3">
                                <Form.Control
                                    aria-label="New Skill name"
                                    placeholder="New Skill name"
                                    value={newSkill.name}
                                    onChange={event => setNewSkill({...newSkill, name: event.target.value})}
                                    required
                                />
                                <Button type="submit" disabled={busy === 'new-skill'}>Add Skill</Button>
                            </Form>
                            {catalogs.skills.map(skill => (
                                <Row key={skill.id} className="worker-setting-row align-items-center g-2">
                                    <Col md={7}><Form.Control value={skill.name} onChange={event => changeRow('skills', skill.id, {name: event.target.value})} /></Col>
                                    <Col md={3}><StatusSelect value={skill.status} onChange={status => changeRow('skills', skill.id, {status})} /></Col>
                                    <Col md={2}><Button className="w-100" variant="outline-primary" disabled={busy === `skill-${skill.id}`} onClick={() => mutate(`skill-${skill.id}`, () => updateSkill(skill.id, skill), 'skills')}>Save</Button></Col>
                                </Row>
                            ))}
                            {catalogs.skills.length === 0 ? <Alert variant="secondary">No Skills configured.</Alert> : null}
                        </Tab>
                        <Tab eventKey="areas" title="Service Areas">
                            <Form onSubmit={addArea} className="mb-3">
                                <Row className="g-2">
                                    <Col md={4}><Form.Control placeholder="Suburb / area" value={newArea.name} onChange={event => setNewArea({...newArea, name: event.target.value})} required /></Col>
                                    <Col md={2}><Form.Control placeholder="Postcode" pattern="[0-9]{4}" value={newArea.postcode} onChange={event => setNewArea({...newArea, postcode: event.target.value})} required /></Col>
                                    <Col md={4}><Form.Select value={newArea.operational_region} onChange={event => setNewArea({...newArea, operational_region: event.target.value})}>{regions.map(region => <option key={region}>{region}</option>)}</Form.Select></Col>
                                    <Col md={2}><Button type="submit" className="w-100" disabled={busy === 'new-area'}>Add Area</Button></Col>
                                </Row>
                            </Form>
                            {catalogs.serviceAreas.map(area => (
                                <Row key={area.id} className="worker-setting-row align-items-center g-2">
                                    <Col md={3}><Form.Control value={area.name} onChange={event => changeRow('serviceAreas', area.id, {name: event.target.value})} /></Col>
                                    <Col md={2}><Form.Control value={area.postcode} pattern="[0-9]{4}" onChange={event => changeRow('serviceAreas', area.id, {postcode: event.target.value})} /></Col>
                                    <Col md={3}><Form.Control value={area.operational_region} readOnly aria-label="Operational Region" /></Col>
                                    <Col md={2}><StatusSelect value={area.status} onChange={status => changeRow('serviceAreas', area.id, {status})} /></Col>
                                    <Col md={2}><Button className="w-100" variant="outline-primary" disabled={busy === `area-${area.id}`} onClick={() => mutate(`area-${area.id}`, () => updateServiceArea(area.id, area), 'serviceAreas')}>Save</Button></Col>
                                </Row>
                            ))}
                            {catalogs.serviceAreas.length === 0 ? <Alert variant="secondary">No Service Areas configured.</Alert> : null}
                        </Tab>
                        <Tab eventKey="durations" title="Duration Types">
                            <Form onSubmit={addDuration} className="mb-3">
                                <Row className="g-2">
                                    <Col md={7}><Form.Control placeholder="New Duration Type" value={newDuration.name} onChange={event => setNewDuration({...newDuration, name: event.target.value})} required /></Col>
                                    <Col md={3}><Form.Control type="number" min={15} max={720} step={15} value={newDuration.default_duration_minutes} onChange={event => setNewDuration({...newDuration, default_duration_minutes: Number(event.target.value)})} required /></Col>
                                    <Col md={2}><Button type="submit" className="w-100" disabled={busy === 'new-duration'}>Add Type</Button></Col>
                                </Row>
                                <Form.Text>Defaults are copied to Properties. Later edits do not overwrite existing Property durations.</Form.Text>
                            </Form>
                            {catalogs.durationTypes.map(duration => (
                                <Row key={duration.id} className="worker-setting-row align-items-center g-2">
                                    <Col md={5}><Form.Control value={duration.name} onChange={event => changeRow('durationTypes', duration.id, {name: event.target.value})} /></Col>
                                    <Col md={3}><Form.Control aria-label={`Default minutes for ${duration.name}`} type="number" min={15} max={720} step={15} value={duration.default_duration_minutes} onChange={event => changeRow('durationTypes', duration.id, {default_duration_minutes: Number(event.target.value)})} /></Col>
                                    <Col md={2}><StatusSelect value={duration.status} onChange={status => changeRow('durationTypes', duration.id, {status})} /></Col>
                                    <Col md={2}><Button className="w-100" variant="outline-primary" disabled={busy === `duration-${duration.id}`} onClick={() => mutate(`duration-${duration.id}`, () => updateDurationType(duration.id, duration), 'durationTypes')}>Save</Button></Col>
                                </Row>
                            ))}
                            {catalogs.durationTypes.length === 0 ? <Alert variant="secondary">No Duration Types configured.</Alert> : null}
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>
        </Container>
    );
}

function StatusSelect({value, onChange}) {
    return (
        <Form.Select aria-label="Catalog status" value={value} onChange={event => onChange(event.target.value)}>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
        </Form.Select>
    );
}

StatusSelect.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};
